import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, LinearProgress } from '@mui/material';
import '../App.css';
import MessagesSummarySection from './messagesSummarySection';
import MessagesTrendsSection from './messagesTrendsSection';
import MessagesTableSection from './messagesTableSection';
import MessagesAnalysisExportSection from './messagesAnalysisExportSection';
import {
  ANESTHESIA_ASSISTANT_APP_ID,
  computeMonthPreset,
  downloadCsv,
  exportTimestamp,
  formatDateTime,
  formatMonth,
  formatMonthLabel,
  getApiBaseUrl,
} from './messagesUtils';

const EMPTY_FILTERS = {
  id: '',
  conversationId: '',
  userId: '',
  email: '',
  role: '',
  isFollowUp: '',
  content: '',
};

const MessagesDashboard = ({ selectedApp }) => {
  const initial = computeMonthPreset('thisMonth');

  const [monthRange, setMonthRange] = useState(initial.monthRange);
  const [startMonth, setStartMonth] = useState(initial.startMonth);
  const [endMonth, setEndMonth] = useState(initial.endMonth);
  const [subscription, setSubscription] = useState('all');

  const [dashboardData, setDashboardData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [tableLoaded, setTableLoaded] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(EMPTY_FILTERS);

  const [chartsLoading, setChartsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [analysisExportLoading, setAnalysisExportLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const dashboardFetchId = useRef(0);
  const messagesFetchId = useRef(0);
  const isFirstLoadRef = useRef(true);

  const periodLabel =
    formatMonth(startMonth) === formatMonth(endMonth)
      ? formatMonthLabel(formatMonth(startMonth))
      : `${formatMonthLabel(formatMonth(startMonth))} – ${formatMonthLabel(formatMonth(endMonth))}`;

  const resetTable = useCallback(() => {
    setTableLoaded(false);
    setMessages([]);
    setMessagesTotal(0);
    setPage(0);
  }, []);

  const buildDashboardParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('app_id', selectedApp);
    params.set('start_month', formatMonth(startMonth));
    params.set('end_month', formatMonth(endMonth));
    params.set('subscription', subscription);
    return params;
  }, [endMonth, selectedApp, startMonth, subscription]);

  const buildQueryParams = useCallback(
    (exportAll = false) => {
      const params = new URLSearchParams();
      params.set('app_id', selectedApp);
      params.set('start_month', formatMonth(startMonth));
      params.set('end_month', formatMonth(endMonth));
      params.set('subscription', subscription);
      if (exportAll) {
        params.set('export', '1');
      } else {
        params.set('page', String(page + 1));
        params.set('limit', String(rowsPerPage));
      }
      Object.entries(debouncedFilters).forEach(([key, value]) => {
        if (value != null && String(value).trim() !== '') {
          params.set(key, String(value));
        }
      });
      return params;
    },
    [debouncedFilters, endMonth, page, rowsPerPage, selectedApp, startMonth, subscription]
  );

  const handleMonthRangeChange = (range) => {
    if (range === 'custom') {
      setMonthRange('custom');
      return;
    }
    const preset = computeMonthPreset(range);
    setMonthRange(preset.monthRange);
    setStartMonth(preset.startMonth);
    setEndMonth(preset.endMonth);
    resetTable();
  };

  const handleStartMonthChange = (date) => {
    if (!date) return;
    const normalized = new Date(date.getFullYear(), date.getMonth(), 1);
    setMonthRange('custom');
    setStartMonth(normalized);
    if (normalized > endMonth) {
      setEndMonth(normalized);
    }
    resetTable();
  };

  const handleEndMonthChange = (date) => {
    if (!date) return;
    const normalized = new Date(date.getFullYear(), date.getMonth(), 1);
    setMonthRange('custom');
    setEndMonth(normalized);
    if (normalized < startMonth) {
      setStartMonth(normalized);
    }
    resetTable();
  };

  const handleSearchChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleSubscriptionChange = (value) => {
    setSubscription(value);
    resetTable();
  };

  const fetchDashboard = useCallback(async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID) return;

    const fetchId = ++dashboardFetchId.current;
    const isInitial = isFirstLoadRef.current;

    if (!isInitial) {
      setRefreshing(true);
    }
    setChartsLoading(true);

    try {
      const allTimeParams = new URLSearchParams();
      allTimeParams.set('app_id', selectedApp);
      allTimeParams.set('subscription', subscription);

      const allTimeRes = await fetch(`${baseUrl}/messages-all-time?${allTimeParams}`);
      if (allTimeRes.ok && fetchId === dashboardFetchId.current) {
        const allTimeData = await allTimeRes.json();
        setDashboardData((prev) => ({
          ...(prev || {}),
          summary: {
            ...(prev?.summary || {}),
            totalAllTime: allTimeData.totalAllTime,
          },
        }));
      }

      const res = await fetch(`${baseUrl}/messages-dashboard?${buildDashboardParams()}`);
      if (!res.ok) {
        throw new Error(`Dashboard request failed: ${res.status}`);
      }
      const data = await res.json();
      if (fetchId !== dashboardFetchId.current) return;
      setDashboardData((prev) => ({
        ...data,
        summary: {
          ...data.summary,
          totalAllTime: prev?.summary?.totalAllTime ?? data.summary?.totalAllTime,
        },
      }));
    } catch (err) {
      console.error('Failed to fetch messages dashboard:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error loading Messages dashboard');
      }
    } finally {
      if (fetchId === dashboardFetchId.current) {
        if (isInitial) {
          isFirstLoadRef.current = false;
        }
        setRefreshing(false);
        setChartsLoading(false);
      }
    }
  }, [buildDashboardParams, selectedApp, subscription]);

  const fetchMessages = useCallback(
    async (exportAll = false) => {
      const baseUrl = getApiBaseUrl();
      if (!baseUrl || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID) return null;

      const fetchId = ++messagesFetchId.current;
      if (!exportAll) {
        setTableLoading(true);
      }

      try {
        const res = await fetch(`${baseUrl}/messages?${buildQueryParams(exportAll)}`);
        if (!res.ok) {
          throw new Error(`Messages request failed: ${res.status}`);
        }
        const data = await res.json();
        if (fetchId !== messagesFetchId.current) return null;

        if (!exportAll) {
          setMessages(Array.isArray(data.messages) ? data.messages : []);
          const hasColumnFilters = Object.values(debouncedFilters).some(
            (value) => value != null && String(value).trim() !== ''
          );
          setMessagesTotal(
            hasColumnFilters
              ? (typeof data.total === 'number' ? data.total : 0)
              : (dashboardData?.summary?.periodTotal ?? (typeof data.total === 'number' ? data.total : 0))
          );
        }
        return data;
      } catch (err) {
        console.error('Failed to fetch messages:', err);
        if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true' && !exportAll) {
          alert('Error loading messages');
        }
        return null;
      } finally {
        if (fetchId === messagesFetchId.current && !exportAll) {
          setTableLoading(false);
        }
      }
    },
    [buildQueryParams, dashboardData?.summary?.periodTotal, debouncedFilters, selectedApp]
  );

  const handleLoadMessages = () => {
    setTableLoaded(true);
    setPage(0);
  };

  const exportChatMessagesTable = async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID || !tableLoaded) return;

    setTableLoading(true);
    try {
      const res = await fetch(`${baseUrl}/messages?${buildQueryParams(true)}`);
      if (!res.ok) {
        throw new Error(`Chat messages export failed: ${res.status}`);
      }
      const data = await res.json();
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      const startLabel = formatMonth(startMonth);
      const endLabel = formatMonth(endMonth);
      downloadCsv(
        rows.map((row) => ({
          id: row.id,
          conversation_id: row.conversationId,
          user_id: row.userId,
          email: row.email || '',
          role: row.role || '',
          is_follow_up: row.isFollowUp ? 'Yes' : 'No',
          created_at: formatDateTime(row.createdAt),
          content: row.content || '',
        })),
        `aa_chat_messages_${startLabel}_${endLabel}_${exportTimestamp()}.csv`
      );
    } catch (err) {
      console.error('Failed to export chat messages table:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error exporting chat messages');
      }
    } finally {
      setTableLoading(false);
    }
  };

  const exportRegisteredForAnalysis = async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID) return;

    setAnalysisExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('app_id', selectedApp);
      params.set('start_month', formatMonth(startMonth));
      params.set('end_month', formatMonth(endMonth));
      params.set('subscription', subscription);
      params.set('source', 'registered');
      params.set('export', '1');

      const res = await fetch(`${baseUrl}/messages?${params}`);
      if (!res.ok) {
        throw new Error(`Registered export failed: ${res.status}`);
      }
      const data = await res.json();
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      const startLabel = formatMonth(startMonth);
      const endLabel = formatMonth(endMonth);
      downloadCsv(
        rows.map((row) => ({
          id: row.id,
          content: row.content || '',
          created_at: formatDateTime(row.createdAt),
          role: row.role || '',
          conversation_id: row.conversationId,
          is_follow_up: row.isFollowUp ? 'true' : 'false',
        })),
        `aa_registered_messages_${startLabel}_${endLabel}_${exportTimestamp()}.csv`
      );
    } catch (err) {
      console.error('Failed to export registered messages:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error exporting registered messages');
      }
    } finally {
      setAnalysisExportLoading(false);
    }
  };

  const exportAllUsersMessages = async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID) return;

    setAnalysisExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('app_id', selectedApp);
      params.set('start_month', formatMonth(startMonth));
      params.set('end_month', formatMonth(endMonth));
      params.set('source', 'all_users');
      params.set('export', '1');

      const res = await fetch(`${baseUrl}/messages?${params}`);
      if (!res.ok) {
        throw new Error(`All-users export failed: ${res.status}`);
      }
      const data = await res.json();
      const rows = Array.isArray(data?.messages) ? data.messages : [];
      const startLabel = formatMonth(startMonth);
      const endLabel = formatMonth(endMonth);
      downloadCsv(
        rows.map((row) => ({
          content: row.content || '',
          bot_id: row.botId || '',
          bot_message: row.botMessage ? 'true' : 'false',
          created_at: formatDateTime(row.createdAt),
        })),
        `aa_all_users_messages_${startLabel}_${endLabel}_${exportTimestamp()}.csv`
      );
    } catch (err) {
      console.error('Failed to export all-users messages:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error exporting all-users messages');
      }
    } finally {
      setAnalysisExportLoading(false);
    }
  };

  const exportFeedback = async () => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID) return;

    setAnalysisExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('app_id', selectedApp);
      params.set('start_month', formatMonth(startMonth));
      params.set('end_month', formatMonth(endMonth));

      const res = await fetch(`${baseUrl}/message-feedback?${params}`);
      if (!res.ok) {
        throw new Error(`Feedback export failed: ${res.status}`);
      }
      const data = await res.json();
      const rows = Array.isArray(data?.feedback) ? data.feedback : [];
      const startLabel = formatMonth(startMonth);
      const endLabel = formatMonth(endMonth);
      downloadCsv(
        rows.map((row) => ({
          message_id: row.messageId,
          reaction: row.reaction || '',
          feedback_text: row.feedbackText || '',
          created_at: formatDateTime(row.createdAt),
        })),
        `aa_message_feedback_${startLabel}_${endLabel}_${exportTimestamp()}.csv`
      );
    } catch (err) {
      console.error('Failed to export message feedback:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error exporting message feedback');
      }
    } finally {
      setAnalysisExportLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 400);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    if (selectedApp !== ANESTHESIA_ASSISTANT_APP_ID) return;
    isFirstLoadRef.current = true;
    setDashboardData(null);
    setFilters(EMPTY_FILTERS);
    setDebouncedFilters(EMPTY_FILTERS);
    const preset = computeMonthPreset('thisMonth');
    setMonthRange(preset.monthRange);
    setStartMonth(preset.startMonth);
    setEndMonth(preset.endMonth);
    setSubscription('all');
    resetTable();
  }, [selectedApp, resetTable]);

  useEffect(() => {
    resetTable();
  }, [startMonth, endMonth, subscription, resetTable]);

  useEffect(() => {
    setDashboardData((prev) => {
      if (!prev?.summary) return prev;
      return {
        ...prev,
        summary: {
          ...prev.summary,
          periodTotal: null,
          userMessages: null,
          followUpMessages: null,
        },
      };
    });
  }, [startMonth, endMonth, subscription]);

  useEffect(() => {
    if (!selectedApp || selectedApp !== ANESTHESIA_ASSISTANT_APP_ID || !startMonth || !endMonth) return;
    const timer = setTimeout(() => {
      fetchDashboard();
    }, 150);
    return () => clearTimeout(timer);
  }, [selectedApp, startMonth, endMonth, subscription, fetchDashboard]);

  useEffect(() => {
    if (!tableLoaded) return;
    const timer = setTimeout(() => {
      fetchMessages();
    }, 200);
    return () => clearTimeout(timer);
  }, [tableLoaded, page, rowsPerPage, debouncedFilters, fetchMessages]);

  const tableTotal = messagesTotal;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
      {refreshing ? (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300 }} />
      ) : null}

      <MessagesSummarySection
        summary={dashboardData?.summary}
        periodLoading={chartsLoading}
        startMonth={formatMonth(startMonth)}
        endMonth={formatMonth(endMonth)}
      />

      <MessagesTrendsSection
        monthlyTrends={dashboardData?.monthlyTrends}
        chartsLoading={chartsLoading}
        monthRange={monthRange}
        startMonth={startMonth}
        endMonth={endMonth}
        subscription={subscription}
        onMonthRangeChange={handleMonthRangeChange}
        onStartMonthChange={handleStartMonthChange}
        onEndMonthChange={handleEndMonthChange}
        onSubscriptionChange={handleSubscriptionChange}
      />

      <MessagesTableSection
        messages={messages}
        total={tableTotal}
        periodLabel={periodLabel}
        tableLoaded={tableLoaded}
        page={page}
        rowsPerPage={rowsPerPage}
        filters={filters}
        tableLoading={tableLoading}
        onLoadMessages={handleLoadMessages}
        onSearchChange={handleSearchChange}
        onPageChange={setPage}
        onRowsPerPageChange={(value) => {
          setRowsPerPage(value);
          setPage(0);
        }}
        onExportTable={exportChatMessagesTable}
      />

      <MessagesAnalysisExportSection
        periodLabel={periodLabel}
        loading={analysisExportLoading}
        onExportRegistered={exportRegisteredForAnalysis}
        onExportAllUsers={exportAllUsersMessages}
        onExportFeedback={exportFeedback}
      />
    </Box>
  );
};

export default MessagesDashboard;
